import React, { useEffect, useState } from 'react'

import { Button, Checkbox, FormattedHTMLMessage, Input, Spinner } from '@web/walter-ui-kit'
import { useIntl } from 'react-intl'
import { Controller, useForm } from 'react-hook-form'
import { GetIssueFormReturn, GetProducts, PostCardsReturn } from '@web/walter-lib/api'
import { v4 as uuidv4 } from 'uuid'
import Big from '@web/walter-lib/big.js'
import { CardsViews } from '../../types'
import PaymentDetails from '../PaymentDetails'
import CurrencyInput from '../CurrencyInput'
import MistakeModal from '../MistakeModal'
import StatusModal from '../StatusModal'
import s from './s.module.css'
import { lang } from './lang'
import { cardsApi, useCards, useProducts, useIssueForm } from '~api'
import { usePromise } from '~hooks/usePromise'
import { useWalletByCurrency } from '~hooks/useWalletByCurrency'
import ExternalLink from '~components/ExternalLink'

interface Props {
    setView: (newView: CardsViews) => void
    data: GetProducts
}

interface FormProps extends Props {
    issueFormData: GetIssueFormReturn
}

const defaultValues = {}

const getStatus = (respStatus: PostCardsReturn['status']) => {
    if (respStatus === 'PENDING') {
        return 'pending'
    }

    if (respStatus === 'UNPAID' || respStatus === 'ACTIVE') {
        return 'success'
    }

    return 'error'
}

// eslint-disable-next-line max-lines-per-function
function Form({ setView, data, issueFormData }: FormProps) {
    const intl = useIntl()

    const { mutate: mutateCards } = useCards()
    const { mutate: mutateProducts } = useProducts()

    const [mistakeState, setMistakeState] = useState({ isOpen: false, fieldName: '' })
    const [statusModal, setStatusModal] = useState<{
        isOpen: boolean
        status: 'pending' | 'success' | 'error'
        cardId?: string
    }>({
        isOpen: false,
        status: 'pending',
    })

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<Record<string, string>>({
        mode: 'onTouched',
        defaultValues,
    })

    const { send, isLoading } = usePromise(
        async (form_data: any) => {
            delete form_data['checkbox']
            const res = await cardsApi.postCards({
                product_id: data.id,
                agreement_id: data.agreement_id || '',
                form_data,
                idempotency_key: uuidv4(),
            })

            setStatusModal({ isOpen: true, status: getStatus(res.status), cardId: res.card_request_id })
        },
        {
            onSuccess: () => {
                mutateCards()
            },
        }
    )

    const onStatusClose = () => {
        if (statusModal.status === 'pending' || statusModal.status === 'success') {
            localStorage.setItem('cards_selectedCard', '')
            setView(CardsViews.Cards)
            mutateCards()
            mutateProducts()
            setStatusModal({ isOpen: false, status: 'pending' })
        } else {
            setStatusModal({ isOpen: false, status: 'pending' })
        }
    }

    const { data: balance, isLoading: isBalanceLoading } = useWalletByCurrency(data.currency)
    const isBalanceError = !isBalanceLoading && Big(balance || 0).lt(Big(data?.fees?.issuance?.fixed || 0))

    const onSubmit = handleSubmit(form => {
        if (isBalanceError) {
            return
        }

        send(form)
    })

    useEffect(() => {
        if (statusModal.isOpen && statusModal.cardId) {
            const interval = setInterval(() => {
                mutateCards(
                    async () => {
                        const cards = await cardsApi.getCards()

                        const findCard = cards?.cards?.find(card => card.id === statusModal.cardId)

                        if (findCard?.status === 'ACTIVE' || findCard?.status === 'UNPAID') {
                            setStatusModal(s => ({ ...s, status: 'success' }))
                        } else if (findCard?.status !== 'PENDING') {
                            setStatusModal(s => ({ ...s, status: 'error' }))
                        }

                        return cards
                    },
                    { revalidate: false }
                )
            }, 4000)

            return () => clearInterval(interval)
        }

        return () => undefined
    }, [mutateCards, statusModal.cardId, statusModal.isOpen])

    return (
        <>
            <StatusModal isOpen={statusModal.isOpen} onClose={onStatusClose} view={statusModal.status} />
            <MistakeModal
                isOpen={mistakeState.isOpen}
                fieldName={mistakeState.fieldName}
                onClose={() => setMistakeState({ isOpen: false, fieldName: '' })}
            />
            <form onSubmit={onSubmit} className={s.form}>
                {issueFormData?.groups?.map((group, i) => (
                    <div key={i} className={s.group}>
                        {group.fields?.map(field => (
                            <Controller
                                name={field.id}
                                key={field.id}
                                control={control}
                                defaultValue={field.value || ''}
                                render={({ field: { ref, ...rest } }) => (
                                    <Input
                                        {...rest}
                                        placeholder={
                                            field.placeholder?.key
                                                ? intl.formatMessage({
                                                      id: field.placeholder.key,
                                                      defaultMessage: field.placeholder?.fallback || '',
                                                  })
                                                : undefined
                                        }
                                        label={
                                            field.label?.key
                                                ? intl.formatMessage({
                                                      id: field.label.key,
                                                      defaultMessage: field.label?.fallback || '',
                                                  })
                                                : undefined
                                        }
                                        dataPointer={`input_${field.id}`}
                                        inputRef={ref}
                                        isInvalid={!!errors?.[field.id]}
                                        readOnly={field.readonly}
                                        showAction
                                        actionContent={<FormattedHTMLMessage {...lang.edit} />}
                                        onAction={() =>
                                            setMistakeState({
                                                isOpen: true,
                                                fieldName: field.label?.key
                                                    ? intl.formatMessage({
                                                          id: field.label.key,
                                                          defaultMessage: field.label?.fallback || '',
                                                      })
                                                    : '',
                                            })
                                        }
                                        className={field.readonly ? s.readOnlyInput : undefined}
                                    />
                                )}
                            />
                        ))}
                        <CurrencyInput data={data} isBalanceError={isBalanceError} />
                    </div>
                ))}
                <PaymentDetails data={data} />
                <Controller
                    name="checkbox"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { ref, value, ...rest }, fieldState }) => (
                        <div className={s.checkboxWrap}>
                            <Checkbox
                                {...rest}
                                inputRef={ref}
                                isChecked={!!value}
                                isError={fieldState.invalid}
                                dataPointer="confirm_checkbox"
                            >
                                <FormattedHTMLMessage {...lang.checkbox} className={s.checkbox} />{' '}
                                <ExternalLink
                                    href={`/cards/${data?.id}/agreements/${data?.agreement_id}`}
                                    className={s.offer}
                                >
                                    <FormattedHTMLMessage {...lang.offer} />
                                </ExternalLink>
                            </Checkbox>
                        </div>
                    )}
                />
                <div className={s.buttonRow}>
                    <div className={s.back}>
                        <Button
                            mode="secondary"
                            htmlButtonType="button"
                            size="m"
                            fullWidth
                            dataPointer="back_button"
                            onClick={() => setView(CardsViews.NewCards)}
                        >
                            <FormattedHTMLMessage {...lang.back} />
                        </Button>
                    </div>
                    <div className={s.getCard}>
                        <Button
                            mode="primary"
                            htmlButtonType="submit"
                            size="m"
                            fullWidth
                            dataPointer="get_card_button"
                            isLoading={isLoading}
                            isDisabled={isBalanceLoading}
                        >
                            <FormattedHTMLMessage {...lang.getCard} />
                        </Button>
                    </div>
                </div>
            </form>
        </>
    )
}

function FormContainer({ setView, data }: Props) {
    const { isLoading: isIssueLoading, data: issueFormData } = useIssueForm({ id: data.id })

    if (isIssueLoading) {
        return (
            <div className={s.spinner}>
                <Spinner size={24} intent="accent" />
            </div>
        )
    }

    if (!issueFormData) {
        return <>Error in data fetching</>
    }

    return <Form setView={setView} data={data} issueFormData={issueFormData} />
}

export default FormContainer
